// The field label dictionary — one Spanish name per database column, shared by
// the audit diff, the upsert forms and the paginated tables.
//
// It exists because the same column used to have a different Spanish name
// depending on the screen: `order_code` was "Código" in the audit and "Folio" on
// the form; `invoice_code` was "Folio de factura" and "Factura". Nobody noticed,
// because nobody sees two surfaces side by side — and it is exactly how the
// audit map drifted 59 columns behind the schema before the 2026-07-31 sweep.
//
// See docs/features/ongoing/feature-activities-audit.md §10.

/**
 * One column, two lengths. The choice between them is NOT per-surface — it is
 * whether the surrounding UI already says which field this is.
 *
 * - `short` — table header and form input. The table is about one entity and a
 *   form section names the half, so "Días de crédito" inside a box titled
 *   *Cliente* reads correctly; "Días de crédito (cliente)" reads as a stutter.
 * - `long` — the audit diff row. It lists twenty fields of mixed provenance in
 *   one column with no headings, so `client_credit_days` and
 *   `supplier_credit_days` have to be distinguishable on their own line.
 *
 * If a surface wants a third string, that is a signal the label is wrong — not
 * that this needs a third variant.
 */
export interface Label {
    /**
     * Optional, and deliberately absent on most entries: it is only filled when
     * the short form is genuinely DIFFERENT from the long one. Duplicating
     * `long` into `short` on the ~100 columns where they agree would recreate
     * the very thing this dictionary removes — two copies of one Spanish word,
     * free to drift apart. Read it through `shortLabel()`, never directly.
     */
    short?: string;
    long: string;
}

/**
 * The reader for every short-label surface: forms (phase 1) and paginated table
 * headers (phase 2).
 *
 * It takes the `Label`, not the key, on purpose. The caller writes
 * `shortLabel(FIELD_LABELS.credit_note_code)`, so a column nobody has named is
 * a compile error at the indexing step — which is the guarantee §10.9 asks for.
 * A `shortLabel('credit_note_code')` overload would have to accept `string` and
 * would give that away.
 */
export function shortLabel(label: Label): string {
    return label.short ?? label.long;
}

/** Keyed by DB column, or by table name in the collections map. */
export type LabelMap = Record<string, Label>;

/**
 * Identity function that checks the shape while PRESERVING the literal keys.
 *
 * Annotating a map `: LabelMap` would widen `keyof typeof` to `string`, and the
 * point of the dictionary is that a consumer asking for a column nobody has
 * named should be a compile error rather than a blank header (§10.9). This
 * repo is on TypeScript 4.4, which predates `satisfies`, so the generic
 * identity function is how that is expressed.
 */
export function defineLabels<T extends LabelMap>(labels: T): T {
    return labels;
}
